import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send, RotateCcw, Check, Play, Sparkles, Loader2, ExternalLink, Link2, Instagram, CalendarClock, Images, Trash2, Zap, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadMediaFiles, mediaDownloadUrls } from "@/lib/mediaDownload";
import { FlagBadges, TypePill } from "@/components/Badges";
import { FileUpload, replaceUploadsInForm, resolveMediaUrl } from "@/components/FileUpload";
import { celebrate } from "@/lib/confetti";
import { AutoTextarea } from "@/components/AutoTextarea";
import { AiInsightPanel } from "@/components/AiInsightPanel";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Freigabe-Center Modal — Hybrid Split: links dunkle Medien-Bühne, rechts helles Formular
export function ApprovalModal({ item, onClose, onChanged }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [metaConnected, setMetaConnected] = useState(false);
  const [dmReady, setDmReady] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [bestTime, setBestTime] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dmDraft, setDmDraft] = useState({ enabled: false, keywords: "", dm_text: "", public_reply_enabled: false, public_reply_text: "Schau in deine DMs! 📩" });
  const [dlPct, setDlPct] = useState(null);

  const downloadMedia = async () => {
    const urls = mediaDownloadUrls(form);
    if (!urls.length || dlPct !== null) return;
    setDlPct(0);
    try {
      await downloadMediaFiles({ urls, title: item.title, onProgress: setDlPct });
    } finally { setDlPct(null); }
  };

  const reorderCarousel = (from, to) => {
    if (from === to || from == null || to == null) return;
    const arr = [...(form.carousel_urls || [])];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    field("carousel_urls", arr);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (item) {
      setForm({
        hook: item.hook, caption: item.caption, cta: item.cta, subtitles: item.subtitles,
        source_link: item.source_link || "", video_url: item.video_url || "", media_url: item.media_url || "",
        carousel_urls: item.carousel_urls || [],
      });
      const d = item.dm_rule_draft || {};
      setDmDraft({
        enabled: !!d.enabled,
        keywords: Array.isArray(d.keywords) ? d.keywords.join(", ") : (d.keywords || ""),
        dm_text: d.dm_text || "",
        public_reply_enabled: !!d.public_reply_enabled,
        public_reply_text: d.public_reply_text || "Schau in deine DMs! 📩",
        rule_id: d.rule_id,
      });
    }
  }, [item]);

  useEffect(() => {
    if (item?.account_key) {
      api.get("/meta/status", { params: { account: item.account_key } })
        .then(({ data }) => setMetaConnected(!!data.connected && !!data.has_instagram))
        .catch(() => setMetaConnected(false));
      api.get("/dm-automation/setup", { params: { account: item.account_key } })
        .then(({ data }) => setDmReady(!!data.ready))
        .catch(() => setDmReady(false));
    }
  }, [item]);

  useEffect(() => {
    if (item?.account_key)
      api.get("/meta/recommendations", { params: { account: item.account_key } })
        .then(({ data }) => {
          if (data?.available && data.best_weekday != null && data.best_hour != null) {
            setBestTime({ weekday: data.best_weekday, hour: data.best_hour, day: data.best_day, label: data.best_time_label });
          } else setBestTime(null);
        })
        .catch(() => setBestTime(null));
  }, [item]);

  const toLocalInput = (d) => {
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60 * 1000).toISOString().slice(0, 16);
  };

  const nextBestSlot = () => {
    if (!bestTime) return null;
    const now = new Date();
    const target = new Date(now);
    target.setHours(bestTime.hour, 0, 0, 0);
    const jsTargetDay = (bestTime.weekday + 1) % 7; // python(Mon=0) -> js(Sun=0)
    const diff = (jsTargetDay - target.getDay() + 7) % 7;
    target.setDate(target.getDate() + diff);
    if (target.getTime() < now.getTime() + 10 * 60 * 1000) target.setDate(target.getDate() + 7);
    return target;
  };

  const applyBestTime = () => {
    const slot = nextBestSlot();
    if (slot) setScheduleAt(toLocalInput(slot));
  };

  useEffect(() => {
    if (scheduleOpen && !scheduleAt && bestTime) applyBestTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleOpen]);

  if (!item || !form) return null;

  const field = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/suggest", {
        account_key: item.account_key,
        title: item.title,
        summary: form.caption || item.hook || "",
        content_type: item.content_type,
      });
      setForm((f) => ({ ...f, hook: data.hook, caption: data.caption, cta: data.cta, subtitles: data.subtitles }));
      toast.success(data.data_backed ? "KI-Vorschläge auf Basis echter Performance-Daten eingefügt" : "KI-Vorschläge eingefügt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Vorschlag fehlgeschlagen");
    } finally {
      setAiBusy(false);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await api.patch(`/content/${item.id}`, { ...form, dm_rule_draft: dmDraftPayload() });
      toast.success("Änderungen gespeichert");
      onChanged?.();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const sendBack = async () => {
    setSaving(true);
    try {
      await api.patch(`/content/${item.id}`, { ...form, status: "creator_working" });
      toast("Zurück an Creator gesendet");
      onChanged?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    setSaving(true);
    try {
      await api.patch(`/content/${item.id}`, { ...form, status: "approved" });
      onChanged?.();
      onClose();
      toast.success("Inhalt freigegeben 🎉");
      // Die visuelle Belohnung darf den abgeschlossenen Freigabe-Flow nie
      // blockieren (z. B. wenn Canvas in einem Browser nicht verfuegbar ist).
      try { celebrate(); } catch { /* Freigabe ist bereits gespeichert. */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Freigabe fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      await api.patch(`/content/${item.id}`, form);
      await api.post(`/content/${item.id}/publish`);
      toast.success("Veröffentlicht 🎉");
      onChanged?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const dmDraftPayload = () => ({
    enabled: dmDraft.enabled,
    keywords: dmDraft.keywords.split(",").map((k) => k.trim()).filter(Boolean),
    dm_text: dmDraft.dm_text,
    public_reply_enabled: dmDraft.public_reply_enabled,
    public_reply_text: dmDraft.public_reply_text,
  });

  const dmDraftInvalid = () => {
    if (!dmDraft.enabled) return false;
    if (!dmReady) {
      toast.error("DM-Automation ist noch nicht vollständig eingerichtet. Prüfe den Status im Menüpunkt DM-Automation.");
      return true;
    }
    if (!dmDraft.keywords.trim() || !dmDraft.dm_text.trim()) {
      toast.error("DM-Automation: Bitte Keyword und DM-Text ausfüllen (oder ausschalten)");
      return true;
    }
    return false;
  };

  const publishToInstagram = async () => {
    if (dmDraftInvalid()) return;
    setPublishing(true);
    try {
      await api.patch(`/content/${item.id}`, { ...form, dm_rule_draft: dmDraftPayload() });
      const { data } = await api.post(`/content/${item.id}/publish-meta`, { platforms: ["instagram", "facebook"] });
      const ig = data.results?.instagram;
      const fb = data.results?.facebook;
      if (ig?.ok) {
        toast.success(dmDraft.enabled ? "Auf Instagram veröffentlicht 🎉 — DM-Regel für den Beitrag aktiviert ⚡" : "Auf Instagram veröffentlicht 🎉");
        if (fb?.ok) toast.success("Auch auf Facebook veröffentlicht ✅");
        else if (fb?.error) toast.warning(`Facebook: ${fb.error}`, { duration: 9000 });
        onChanged?.();
        onClose();
      } else {
        toast.error(`Instagram: ${ig?.error || "Veröffentlichung fehlgeschlagen"}`);
        if (fb?.error) toast.warning(`Facebook: ${fb.error}`, { duration: 9000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Veröffentlichung fehlgeschlagen");
    } finally {
      setPublishing(false);
    }
  };

  const scheduleToInstagram = async () => {
    if (!scheduleAt) {
      toast.error("Bitte einen Zeitpunkt wählen");
      return;
    }
    if (dmDraftInvalid()) return;
    setScheduling(true);
    try {
      await api.patch(`/content/${item.id}`, { ...form, dm_rule_draft: dmDraftPayload() });
      const iso = new Date(scheduleAt).toISOString();
      await api.post(`/content/${item.id}/schedule-meta`, { platforms: ["instagram", "facebook"], scheduled_publish_at: iso });
      toast.success(`Für ${new Date(scheduleAt).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })} eingeplant 📅`);
      setScheduleOpen(false);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Planung fehlgeschlagen");
    } finally {
      setScheduling(false);
    }
  };

  const minScheduleAt = (() => {
    const d = new Date(Date.now() + 5 * 60 * 1000);
    d.setSeconds(0, 0);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60 * 1000).toISOString().slice(0, 16);
  })();

  const hasMedia = !!(form.video_url || form.media_url || (form.carousel_urls || []).length > 0);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 bg-neutral-900/70" onClick={onClose}>
      <div
        data-testid="approval-modal"
        className="w-full max-w-[1200px] h-[95dvh] md:h-[88vh] flex flex-col md:flex-row bg-white rounded-t-2xl md:rounded-2xl overflow-hidden shadow-2xl animate-rise"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Links: dunkle Medien-Bühne */}
        <div className="w-full md:w-[360px] lg:w-[420px] bg-neutral-950 flex flex-col shrink-0 h-[300px] md:h-auto relative">
          <div className="flex items-center justify-between px-4 pt-4 z-10">
            <TypePill type={item.content_type} />
            {hasMedia && (
              <button
                data-testid="approval-download-btn"
                onClick={downloadMedia}
                disabled={dlPct !== null}
                title={mediaDownloadUrls(form).length > 1 ? "Alle hinterlegten Medien gemeinsam als ZIP herunterladen" : "Medium herunterladen"}
                className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-3 py-1.5 transition-colors disabled:opacity-70"
              >
                {dlPct !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {dlPct !== null ? `${dlPct}%` : mediaDownloadUrls(form).length > 1 ? `Alle Medien (${mediaDownloadUrls(form).length}) als ZIP` : "Download"}
              </button>
            )}
          </div>

          <div className="flex-1 min-h-0 flex items-center justify-center p-4 md:p-6">
            {form.video_url ? (
              <div className="relative h-full aspect-[9/16] max-w-full rounded-xl overflow-hidden bg-neutral-900 ring-1 ring-white/10 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
                <video
                  data-testid="approval-video"
                  src={`${resolveMediaUrl(form.video_url)}#t=0.1`}
                  controls
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-contain bg-black"
                />
              </div>
            ) : item.content_type === "carousel" && (form.carousel_urls || []).length > 0 ? (
              <div className="relative h-full max-h-full aspect-square max-w-full rounded-xl overflow-hidden bg-neutral-900 ring-1 ring-white/10">
                <img src={resolveMediaUrl(form.carousel_urls[0])} alt={item.title} className="h-full w-full object-cover" loading="lazy" />
                <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-black/60 px-2.5 py-1 rounded-full">
                  <Images className="h-3.5 w-3.5" /> {form.carousel_urls.length} Medien
                </span>
              </div>
            ) : form.media_url ? (
              <div className="relative max-h-full max-w-full rounded-xl overflow-hidden ring-1 ring-white/10">
                <img src={resolveMediaUrl(form.media_url)} alt={item.title} className="max-h-full max-w-full object-contain" loading="lazy" />
              </div>
            ) : (
              <div data-testid="approval-no-media" className="flex flex-col items-center gap-2 text-neutral-500 border-2 border-dashed border-neutral-800 rounded-xl px-10 py-12">
                <Play className="h-8 w-8" />
                <span className="text-xs">Noch kein Medium — unten hochladen</span>
              </div>
            )}
          </div>

          <div className="px-4 pb-4">
            <FileUpload
              dark
              accept="video/*,image/*"
              label={form.video_url ? "Video ersetzen" : "Video / Bild hochladen"}
              folderTitle={item?.title || "Allgemein"}
              multiple
              onBatchUploaded={(items) => setForm((current) => replaceUploadsInForm(current, items))}
            />
          </div>
        </div>

        {/* Rechts: helles Formular mit fixem Header + Footer */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-white">
          <div className="px-4 md:px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0 bg-white">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Freigabe-Center</p>
              <h3 className="font-head font-semibold tracking-tight text-lg md:text-xl text-slate-900 leading-tight mt-0.5 truncate">{item.title}</h3>
              <div className="mt-1.5"><FlagBadges flags={item.flags} /></div>
            </div>
            <button data-testid="approval-modal-close" onClick={onClose} className="h-9 w-9 shrink-0 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-4 bg-slate-50/60">
            {/* Karte: Texte & Inhalt */}
            <section className="bg-white rounded-xl border border-slate-200 p-4 md:p-5 space-y-4" data-testid="approval-section-texts">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Texte & Inhalt</p>
                <button
                  data-testid="btn-ai-suggest"
                  onClick={runAI}
                  disabled={aiBusy}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-60"
                >
                  {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  KI-Vorschläge
                </button>
              </div>

              <LightField label="Hook" value={form.hook} onChange={(v) => field("hook", v)} testid="field-hook" />
              <LightField label="Caption" value={form.caption} onChange={(v) => field("caption", v)} rows={3} maxH testid="field-caption" />
              <LightField label="CTA" value={form.cta} onChange={(v) => field("cta", v)} testid="field-cta" />
              <LightField label="Untertitel" value={form.subtitles} onChange={(v) => field("subtitles", v)} rows={2} mono maxH testid="field-subtitles" />

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" /> Content-Link (z. B. OneDrive)
                </label>
                <div className="mt-1.5 flex gap-2">
                  <input
                    data-testid="field-source-link"
                    value={form.source_link || ""}
                    onChange={(e) => field("source_link", e.target.value)}
                    placeholder="https://onedrive.live.com/…"
                    className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                  />
                  {form.source_link ? (
                    <a
                      data-testid="btn-open-link"
                      href={form.source_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium px-3 rounded-lg bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" /> Öffnen
                    </a>
                  ) : null}
                </div>
              </div>
            </section>

            {/* Karte: Karussell */}
            {item.content_type === "carousel" && (
              <section data-testid="carousel-manager" className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Images className="h-3.5 w-3.5" /> Karussell-Medien ({(form.carousel_urls || []).length}/10)
                  </label>
                  <FileUpload
                    accept="image/*,video/*"
                    label="Hinzufügen"
                    multiple
                    onBatchUploaded={(items) =>
                      setForm((s) => ({ ...s, carousel_urls: [...(s.carousel_urls || []), ...items.map((d) => d.url)].slice(0, 10) }))
                    }
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Mindestens 2 Medien — per Drag &amp; Drop sortieren. Erscheint als ein Beitrag zum Durchwischen.</p>
                {(form.carousel_urls || []).length > 0 && (
                  <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="carousel-items">
                    {(form.carousel_urls || []).map((url, i) => (
                      <div
                        key={`${url}-${i}`}
                        draggable
                        onDragStart={() => setDragIdx(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => { reorderCarousel(dragIdx, i); setDragIdx(null); }}
                        onDragEnd={() => setDragIdx(null)}
                        className={`relative group aspect-square rounded-md overflow-hidden border bg-slate-100 cursor-grab active:cursor-grabbing transition-colors ${dragIdx === i ? "border-amber-500 opacity-50" : "border-slate-200"}`}
                      >
                        {/\.(mp4|mov)$/i.test(url)
                          ? <video src={resolveMediaUrl(url)} className="h-full w-full object-cover" muted preload="metadata" />
                          : <img src={resolveMediaUrl(url)} alt="" className="h-full w-full object-cover" loading="lazy" />}
                        <span className="absolute top-1 left-1 text-[10px] font-bold bg-black/70 text-white rounded px-1">{i + 1}</span>
                        <button
                          data-testid={`carousel-remove-${i}`}
                          onClick={() => field("carousel_urls", (form.carousel_urls || []).filter((_, x) => x !== i))}
                          className="absolute top-1 right-1 h-5 w-5 rounded bg-black/70 text-rose-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Karte: Comment-to-DM */}
            {metaConnected && (
              <section data-testid="dm-draft-section" className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5 text-amber-500" /> Comment-to-DM für diesen Beitrag
                  </label>
                  <button
                    type="button"
                    data-testid="dm-draft-toggle"
                    disabled={!dmReady}
                    onClick={() => setDmDraft((s) => ({ ...s, enabled: !s.enabled }))}
                    className={`relative h-5 w-9 rounded-full transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${dmDraft.enabled ? "bg-amber-500" : "bg-slate-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${dmDraft.enabled ? "translate-x-[16px]" : ""}`} />
                  </button>
                </div>
                {!dmReady && (
                  <p data-testid="dm-draft-not-ready" className="text-[11px] text-amber-700 mt-1.5">
                    Noch nicht einsatzbereit — bitte zuerst die Einrichtung unter „DM-Automation“ abschließen.
                  </p>
                )}
                {dmDraft.rule_id ? (
                  <p className="text-[11px] text-emerald-600 mt-1.5">✓ DM-Regel wurde bei der Veröffentlichung angelegt — verwalten unter „DM-Automation“.</p>
                ) : dmDraft.enabled ? (
                  <div className="mt-3 space-y-2.5">
                    <input
                      data-testid="dm-draft-keywords"
                      value={dmDraft.keywords}
                      onChange={(e) => setDmDraft((s) => ({ ...s, keywords: e.target.value }))}
                      placeholder="Keywords, z.B. GUIDE, LINK"
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                    />
                    <AutoTextarea
                      data-testid="dm-draft-text"
                      minRows={3}
                      value={dmDraft.dm_text}
                      onChange={(v) => setDmDraft((s) => ({ ...s, dm_text: v }))}
                      placeholder={"DM-Text, z.B.: Hey {username}! 👋 Hier ist dein Link: https://…"}
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                    />
                    <label className="flex items-center gap-2 text-[12px] text-slate-500 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="dm-draft-public-reply"
                        checked={dmDraft.public_reply_enabled}
                        onChange={(e) => setDmDraft((s) => ({ ...s, public_reply_enabled: e.target.checked }))}
                        className="accent-amber-500"
                      />
                      Zusätzlich öffentlich antworten
                    </label>
                    {dmDraft.public_reply_enabled && (
                      <input
                        data-testid="dm-draft-public-reply-text"
                        value={dmDraft.public_reply_text}
                        onChange={(e) => setDmDraft((s) => ({ ...s, public_reply_text: e.target.value }))}
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                      />
                    )}
                    <p className="text-[11px] text-slate-400">Wird beim Posten (sofort oder geplant) automatisch als Regel nur für diesen Beitrag aktiviert.</p>
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1.5">Kommentiert jemand ein Keyword unter diesem Beitrag, bekommt er automatisch eine private DM.</p>
                )}
              </section>
            )}

            {/* Karte: KI-Insights (dunkles Panel behält seinen Look) */}
            <section className="bg-neutral-900 rounded-xl p-1.5" data-testid="approval-section-ai">
              <AiInsightPanel accountKey={item.account_key} content={{ ...form, title: item.title, content_type: item.content_type }} />
            </section>
          </div>

          {/* Sticky Aktionsleiste */}
          <div className="shrink-0 border-t border-slate-200 bg-white shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.06)]">
            {!metaConnected && (
              <div data-testid="meta-disconnected-hint" className="px-4 md:px-6 pt-2.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border-b border-amber-100 pb-2.5">
                ⚠️ Kein Instagram-Konto verbunden — »Jetzt posten« &amp; »Später planen« deaktiviert. Unter Einstellungen → Instagram neu verbinden.
              </div>
            )}
            <div className="px-4 md:px-6 py-3 flex flex-wrap items-center gap-2">
              <button data-testid="btn-save-changes" onClick={saveChanges} disabled={saving}
                className="text-sm font-medium px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
                Speichern
              </button>
              <button data-testid="btn-send-back" onClick={sendBack} disabled={saving}
                className="text-sm font-medium px-3 py-2 rounded-lg text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                <RotateCcw className="h-4 w-4" /> An Creator
              </button>
              <div className="flex-1" />
              <button data-testid="btn-approve" onClick={approve} disabled={saving}
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                <Check className="h-4 w-4" /> Freigeben
              </button>
              <button data-testid="btn-publish" onClick={publish} disabled={saving}
                className="text-sm font-semibold px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                <Send className="h-4 w-4" /> Veröffentlichen
              </button>
              {metaConnected && (
                <AlertDialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
                  <AlertDialogTrigger asChild>
                    <button data-testid="btn-schedule-instagram" disabled={saving || publishing || scheduling}
                      className="text-sm font-semibold px-4 py-2 rounded-lg border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                      {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Später planen
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Instagram-Beitrag einplanen</AlertDialogTitle>
                      <AlertDialogDescription>
                        Der Beitrag wird zum gewählten Zeitpunkt <strong>automatisch und öffentlich</strong> auf dem verbundenen Instagram-Konto veröffentlicht. Zeitpunkt: 5&nbsp;Min. bis 75&nbsp;Tage in der Zukunft.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-1">
                      <label className="text-xs font-semibold text-neutral-600">Veröffentlichungszeitpunkt</label>
                      <input
                        data-testid="schedule-datetime-input"
                        type="datetime-local"
                        value={scheduleAt}
                        min={minScheduleAt}
                        onChange={(e) => setScheduleAt(e.target.value)}
                        className="mt-1.5 w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                      />
                      {bestTime && (
                        <button
                          type="button"
                          data-testid="apply-best-time-btn"
                          onClick={applyBestTime}
                          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-100 hover:bg-fuchsia-100 rounded-lg px-2.5 py-1.5 transition-colors"
                        >
                          <Sparkles className="h-3.5 w-3.5" /> Beste Zeit übernehmen · {bestTime.day}, {bestTime.label}
                        </button>
                      )}
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction data-testid="btn-schedule-instagram-confirm"
                        onClick={(e) => { e.preventDefault(); scheduleToInstagram(); }}
                        className="bg-teal-600 hover:bg-teal-500">
                        Einplanen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {metaConnected && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="btn-publish-instagram" disabled={saving || publishing}
                      className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#E1306C] text-white hover:bg-[#C13584] transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                      {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" />} Jetzt posten
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Jetzt LIVE auf Instagram veröffentlichen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Dieser Beitrag wird <strong>sofort und öffentlich</strong> auf dem verbundenen Instagram-Konto gepostet und kann nicht zurückgenommen werden. Stelle sicher, dass Medium, Hook und Caption final sind.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction data-testid="btn-publish-instagram-confirm" onClick={publishToInstagram}
                        className="bg-[#E1306C] hover:bg-[#C13584]">
                        Ja, jetzt posten
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function LightField({ label, value, onChange, rows, mono, maxH, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      {rows ? (
        <AutoTextarea
          data-testid={testid}
          minRows={rows}
          value={value || ""}
          onChange={onChange}
          className={`mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 ${mono ? "font-mono text-xs" : ""} ${maxH ? "max-h-44 overflow-y-auto" : ""}`}
        />
      ) : (
        <input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
        />
      )}
    </div>
  );
}
